/** Representative unit price for a product. Pricing is stored flat
 * (`price` for Standard/Package); Volume falls back to the first tier; very
 * old data may still use a nested `products[]` pricing-model array. */
export const productPrice = (product: {
  price?: number;
  pricingModel?: string;
  volumeTiers?: Array<{ price?: number }>;
  products?: Array<{ pricingModel?: string; price?: number }>;
}): number | undefined => {
  if (typeof product.price === "number") return product.price;
  if (Array.isArray(product.volumeTiers) && product.volumeTiers.length) {
    const p = product.volumeTiers[0]?.price;
    if (typeof p === "number") return p;
  }
  const models = product.products ?? [];
  const standard = models.find((m) => m.pricingModel === "Standard");
  const chosen = standard ?? models[0];
  return typeof chosen?.price === "number" ? chosen.price : undefined;
};

/** Format a numeric amount as currency, tolerant of unknown currency codes. */
export const money = (amount: number | undefined, currency: string): string => {
  if (typeof amount !== "number") return "—";
  const code = (currency || "USD").toUpperCase(); // backend may return lowercase
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
};

/** A single pricing object — a product or a delivery variant (same shape). */
interface PricingLike {
  pricingModel?: string;
  price?: number;
  minUnits?: number;
  maxUnits?: number;
  volumeTiers?: Array<{ from?: number; price?: number }>;
}

/** Representative unit price for ONE pricing object, honouring its model:
 * Standard/Package use the flat `price`; Volume uses the lowest tier price. */
const repUnitPrice = (o: PricingLike): number | undefined => {
  if ((o.pricingModel || "Standard") === "Volume") {
    const tiers = Array.isArray(o.volumeTiers) ? o.volumeTiers : [];
    const prices = tiers.map((t) => t?.price).filter((p): p is number => typeof p === "number");
    return prices.length ? Math.min(...prices) : undefined;
  }
  return typeof o.price === "number" ? o.price : undefined;
};

export interface PricingSummary {
  /** Model-aware, formatted price (e.g. "£10", "from £8", "£25 / 1–10 photos",
   *  or a "£8–£12" range across delivery variants). */
  price: string;
  /** Number of delivery variants configured (0 = single/legacy product). */
  variantCount: number;
}

/** Build a price label that reflects the product's ACTUAL pricing model
 * (Standard flat, Package per-pack, Volume "from" the cheapest tier) and, when
 * delivery variants are configured, the price RANGE across those variants —
 * instead of a single flat `price` that misrepresents tiered/variant products. */
export const pricingSummary = (
  product: PricingLike & { variants?: PricingLike[] | null },
  currency: string,
): PricingSummary => {
  const variants = Array.isArray(product.variants) ? product.variants.filter(Boolean) : [];

  if (variants.length > 0) {
    const reps = variants.map(repUnitPrice).filter((p): p is number => typeof p === "number");
    if (reps.length === 0) return { price: "—", variantCount: variants.length };
    const min = Math.min(...reps);
    const max = Math.max(...reps);
    return {
      price: min === max ? money(min, currency) : `${money(min, currency)}–${money(max, currency)}`,
      variantCount: variants.length,
    };
  }

  const model = product.pricingModel || "Standard";
  const rep = repUnitPrice(product);

  if (model === "Volume") {
    return { price: rep != null ? `from ${money(rep, currency)}` : "—", variantCount: 0 };
  }
  if (model === "Package") {
    const lo = product.minUnits;
    const hi = product.maxUnits;
    const range =
      typeof lo === "number"
        ? typeof hi === "number" && hi !== lo
          ? ` / ${lo}–${hi} photos`
          : ` / ${lo} photos`
        : "";
    return { price: `${money(rep, currency)}${range}`, variantCount: 0 };
  }
  return { price: money(rep, currency), variantCount: 0 };
};
